import React, { useCallback, useEffect, useState } from 'react';
import { CONTENT_TOP } from 'dashboard/layouts/admin/chromeGeometry';
import {
  Box,
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useOutletContext } from 'react-router';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import {
  Code,
  EmptyState,
  LoadingOverlay,
  PageHeader,
  PagePaper,
  StatusChip,
} from 'dashboard/components/common';
import { timeAgo } from 'utilities/format';
import CreateApiKeyDialog from './CreateApiKeyDialog';

const KEY_STATUS_COLORS = { active: 'success', revoked: 'error' };

/**
 * Brand API keys list. Owners create read-scoped credentials for their
 * own integrations; the plaintext key is shown exactly once via the
 * `CreateApiKeyDialog`.
 */
export default function ApiKeys() {
  const [selectedBrand] = useOutletContext();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    if (!selectedBrand) return Promise.resolve();
    setLoading(true);
    return API.listBrandApiKeys({ brand_id: selectedBrand })
      .then((res) => setItems(Array.isArray(res) ? res : []))
      .catch((err) => toast.error(err?.message || 'Failed to load keys', { toastId: 'apikeys-load-error' }))
      .finally(() => setLoading(false));
  }, [selectedBrand]);

  // `cancelled` flag in cleanup suppresses state writes after unmount
  // (e.g. StrictMode double-mount in dev, brand switcher mid-flight).
  // Action-triggered re-fetches still call `load()` directly.
  useEffect(() => {
    if (!selectedBrand) return undefined;
    let cancelled = false;
    setLoading(true);
    API.listBrandApiKeys({ brand_id: selectedBrand })
      .then((res) => { if (!cancelled) setItems(Array.isArray(res) ? res : []); })
      .catch((err) => { if (!cancelled) toast.error(err?.message || 'Failed to load keys', { toastId: 'apikeys-load-error' }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedBrand]);

  const revoke = (k) => {
    if (!window.confirm(`Revoke ${k.name || k.prefix}? Requests using it will start returning 401.`)) return;
    API.revokeBrandApiKey({ brand_id: selectedBrand, _id: k._id })
      .then(() => { toast.success('Revoked'); load(); })
      .catch((err) => toast.error(err?.message || 'Failed'));
  };

  const remove = (k) => {
    if (!window.confirm('Delete this key entry permanently?')) return;
    API.deleteBrandApiKey({ brand_id: selectedBrand, _id: k._id })
      .then(() => { toast.success('Deleted'); load(); })
      .catch((err) => toast.error(err?.message || 'Failed'));
  };

  return (
    <Box sx={{ pt: CONTENT_TOP }}>
      <PagePaper>
        <PageHeader
          title='API keys'
          subtitle='Read-only access to this brand for your own integrations. Pair with the webhook signing secret for two-way flows.'
          actions={
            <Button variant='contained' startIcon={<AddIcon />} onClick={() => setOpen(true)}>
              New API key
            </Button>
          }
        />

        {!selectedBrand && <EmptyState>Select a brand.</EmptyState>}

        {selectedBrand && (
          <LoadingOverlay loading={loading && items.length === 0} minHeight={160}>
          <TableContainer
            component={Box}
            sx={{
              border: '1px solid', borderColor: 'divider',
              borderRadius: '12px', overflowX: 'auto',
            }}
          >
            <Table size='medium'>
              <TableHead sx={{ bgcolor: 'action.hover' }}>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Key</TableCell>
                  <TableCell>Scopes</TableCell>
                  <TableCell>Last used</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align='right'>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}><EmptyState>Loading…</EmptyState></TableCell>
                  </TableRow>
                )}
                {!loading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}><EmptyState>No keys yet.</EmptyState></TableCell>
                  </TableRow>
                )}
                {items.map((k) => (
                  <TableRow key={k._id}>
                    <TableCell>
                      <Typography fontWeight='600'>{k.name || '—'}</Typography>
                      <Typography variant='caption' color='text.secondary'>
                        Created {k.createdAt ? new Date(k.createdAt).toLocaleDateString() : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Code>tpo_{k.prefix}_…</Code>
                    </TableCell>
                    <TableCell>
                      <Typography variant='caption'>
                        {k.scopes && k.scopes.length > 0 ? k.scopes.join(', ') : 'all read scopes'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='caption' color='text.secondary'>{timeAgo(k.last_used_at)}</Typography>
                      {k.last_used_ip && (
                        <Typography variant='caption' color='text.secondary' display='block'>
                          {k.last_used_ip}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={k.status} colors={KEY_STATUS_COLORS} />
                    </TableCell>
                    <TableCell align='right'>
                      <Stack direction='row' spacing='4px' justifyContent='flex-end'>
                        {k.status === 'active' && (
                          <Button size='small' variant='outlined' color='warning' onClick={() => revoke(k)}>
                            Revoke
                          </Button>
                        )}
                        <Tooltip title='Delete'>
                          <IconButton size='small' color='error' onClick={() => remove(k)}>
                            <DeleteIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          </LoadingOverlay>
        )}
      </PagePaper>

      <CreateApiKeyDialog
        open={open}
        onClose={() => setOpen(false)}
        brandId={selectedBrand}
        onSaved={load}
      />
    </Box>
  );
}
